import { SetHeader } from "@/components/organisms/SBCs/SbcSetHeader/SetHeader";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import { useGoBack } from "@/hooks/useGoBack";
import { SbcSetView } from "@/pages/Sbcs/SbcSetPage/views/SbcSetView";
import { showModal } from "@/redux/message/messageSlice";
import { getSbcSetSelector, getSbcsSelector } from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { normalizeEaString, sbcSetPath } from "@repo/shared/utils";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  handleCompleteToggle,
  handleFavoriteToggle,
  handleHideToggle,
  handleVote,
} from "./handlers/sbcSetHandlers";

export function formatSbcSetName(name?: string): string {
  if (!name) return "...";
  return normalizeEaString(name)
    .toLowerCase()
    .replace(/-/g, " ")
    .normalize("NFD")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const SbcSetPage = () => {
  const { t } = useTranslation("sbcs");
  const dispatch = useDispatch<AppDispatch>();
  const { sbcs, sbcSetStatus } = useSelector(getSbcsSelector);
  const { setId, name } = useParams();
  const sbcSet = useSelector(getSbcSetSelector(+setId));
  const isMobile = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const navigate = useNavigate();
  const { user } = useSelector(getUserSelector);

  useEffect(() => {
    if (sbcSetStatus === "rejected") navigate("/");
    window.scrollTo(0, 0);
  }, [sbcSetStatus, navigate]);

  const onClickSetToggle = (toggleType) => {
    if (!user.uuid) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook:
            toggleType === "completed"
              ? "complete-sbc"
              : toggleType === "favorite"
                ? "favorite-sbc"
                : "hide-sbc",
        }),
      );
      return;
    }

    if (toggleType === "favorite") handleFavoriteToggle(dispatch, sbcSet, t);
    if (toggleType === "hidden") handleHideToggle(dispatch, sbcSet, t);
    if (toggleType === "completed") handleCompleteToggle(dispatch, sbcSet, t);
  };

  const setHeader = (
    <SetHeader
      sbcSet={sbcSet}
      platform={user.platform}
      onClickSbcToggle={onClickSetToggle}
      onVote={(vote) => handleVote(dispatch, sbcSet, vote, user.uuid)}
      isOnSbcPage={true}
    />
  );

  // Root of the /sbc set tree, so deep links belong on the SBC index.
  const goBack = useGoBack({ fallback: "/sbc" });

  const pageTitle = t("setPage.mobileTitle", {
    game: GAME_NAME,
    name: sbcSet?.name ?? formatSbcSetName(name),
  });
  const canonicalPath = sbcSetPath({ id: +setId, name: "", slug: name });

  return (
    <main className="min-h-[calc(100vh+112px)] flex justify-center overflow-x-hidden">
      {/* One header for both layouts, so title, description, canonical and
          og tags do not depend on the viewport. Desktop gets the overlay bar,
          mobile the persistent top nav. */}
      <HeroSection
        helmetTitle={pageTitle}
        helmetDescription={sbcSet?.description || pageTitle}
        helmetCanonicalLink={canonicalUrl(canonicalPath)}
        back={goBack}
        hidePageDescription
      />
      <div className="w-full flex flex-col bg-background">
        <SbcSetView
          sbcSet={sbcSet}
          setHeader={setHeader}
          sbcs={sbcs}
          isTouch={isMobile}
        />
      </div>
    </main>
  );
};

export default SbcSetPage;
